import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { API_URL, fetchWithAuth, getImageUrl } from '../api';

export default function QRRequestConfirmation() {
  const { eventName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isExiting, setIsExiting] = useState(false);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get event name from localStorage
    const storedEventName = localStorage.getItem('lastEventName');
    
    // Decode the event name from URL if needed
    const decodedEventName = eventName ? decodeURIComponent(eventName) : storedEventName;
    
    // Fetch event data to get the image
    const fetchEvent = async () => {
      try {
        // We need to find the event by name since we only have the name
        const response = await fetchWithAuth(`${API_URL}/events`);
        if (!response.ok) throw new Error('Failed to fetch events');
        const events = await response.json();
        const eventData = events.find(e => e.name === decodedEventName);
        if (eventData) {
          setEvent(eventData);
        }
      } catch (err) {
        console.error('Error fetching event:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();

    // Cleanup localStorage on unmount
    return () => {
      localStorage.removeItem('lastEventName');
    };
  }, [eventName]);

  const handleBack = () => {
    setIsExiting(true);
    setTimeout(() => {
      navigate(location.state?.prevPath || '/');
    }, 300);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-black overflow-hidden text-white relative flex flex-col"
    >
      
      {/* Blurred background and overlay */}

      {event && event.image_url ? (
        <>
          <div 
            className="fixed inset-0 w-full h-full z-0 pointer-events-none"
            style={{ 
              backgroundImage: `url(${getImageUrl(event.image_url)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'top',
              backgroundAttachment: 'fixed',
              filter: 'blur(20px) brightness(1.2) saturate(3)',
              minHeight: '100vh'
            }}
          />
          <div 
            className="fixed inset-0 w-full h-full z-10"
            style={{
              background: 'linear-gradient(to top, rgb(0, 0, 0) 0%, rgb(0, 0, 0) 5%, transparent 100%)',
            }}
          />
        </>
      ) : (
        <div className="fixed inset-0 w-full h-full z-0 bg-black" />
      )}

      {/* Main content */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className={`absolute z-20 flex items-center justify-center px-4 w-full h-full ${isExiting ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300 overflow-hidden`}
      >
        <div className="max-w-2xl mx-auto">
          <div className="bg-black/40 backdrop-blur-sm rounded-xl p-8 shadow-[0_0_15px_rgba(255,255,255,0.1)]">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-6 text-white/80">
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  className="w-full h-full"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
              </div>
              
              <h1 
                className="text-4xl font-bold mb-4 tracking-tight text-white"
                style={{ 
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                Código QR Solicitado
              </h1>
              
              <p 
                className="text-xl mb-4"
                style={{ 
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                <span className="text-gray-400 pr-2">para: </span>
                <span className="text-white text-2xl ">{eventName ? decodeURIComponent(eventName) : eventName}</span>
              </p>
              
              <p 
                className="text-gray-400 text-lg mb-8"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Tu solicitud ha sido enviada a los promotores. 
                Por favor espere a su aprobación.
              </p>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleBack}
                className="px-8 py-3 rounded-lg font-medium tracking-wide text-white transition-all bg-white/10 text-black hover:bg-white/20"
                style={{ 
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                Back to Event
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
} 